import { ErrorTypeForSearch } from "entities/Form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as IconAlert } from "../../assets/alert.svg";
import { ReactComponent as IconFail } from "../../assets/fail.svg";
import { ReactComponent as IconPending } from "../../assets/pending.svg";
import { ReactComponent as IconSuccess } from "../../assets/success.svg";
import Button from "../../components/Button";
import { AppointmentResult, RESULT } from "../../entities/Result";
import styles from "./Result.module.scss";

interface ResultProps {
  result: AppointmentResult;
  onReStart: () => void;
  onEdit: () => void;
  onSearchAnotherAppointment?: () => void;
  effectiveSiteId?: string;
}

const Icon: { [key: number]: JSX.Element } = {
  [RESULT.SUCCESS]: <IconSuccess />,
  [RESULT.PENDING]: <IconPending />,
  [RESULT.FAIL]: <IconFail />,
  [RESULT.CANCELLED]: <IconSuccess className={styles.cancelledIcon} />,
  [RESULT.ERROR]: <IconAlert />,
};

const InfoBoxColor: { [key: number]: string } = {
  [RESULT.SUCCESS]: "#EDF7D7",
  [RESULT.PENDING]: "#FCF1DB",
  [RESULT.FAIL]: "#FFEAEA",
  [RESULT.CANCELLED]: "#D2D6D6",
};

const Result: React.FC<ResultProps> = ({
  result,
  onReStart,
  onEdit,
  onSearchAnotherAppointment,
  effectiveSiteId,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const cmsSiteId =
    effectiveSiteId ??
    searchParams.get("site") ??
    searchParams.get("cmsSiteId") ??
    "";

  const Title: { [key: number]: string } = useMemo(() => {
    return {
      [RESULT.SUCCESS]: t("appointmentSuccess"),
      [RESULT.PENDING]: t("applicationProcessing"),
      [RESULT.FAIL]: t("submissionUnsuccessful"),
      [RESULT.CANCELLED]: t("appointmentHasBeenCancelled"),
      [RESULT.ERROR]:
        result?.error === ErrorTypeForSearch.NotFound
          ? t("appointmentNotFound")
          : result?.error === ErrorTypeForSearch.Cancelled
          ? t("appointmentAlreadyCancelled")
          : result?.error === ErrorTypeForSearch.Expired
          ? t("appointmentExpired")
          : result?.error === ErrorTypeForSearch.MaxChangesExceeded
          ? t("appointmentEditLimitReached")
          : "",
    };
  }, [result?.error, t]);

  const Label: { [key: number]: React.ReactNode } = {
    [RESULT.SUCCESS]: (
      <span>
        {t("appointmentDetailsSaved")}
        <br />
        {t("inquiriesContact", { phoneNo: result.enquiryPhoneNo })}
      </span>
    ),
    [RESULT.PENDING]: (
      <span>
        {t("processingApplication")}
        <br />
        {t("inquiriesContact", { phoneNo: result.enquiryPhoneNo })}
      </span>
    ),
    [RESULT.FAIL]: (
      <span>
        {t("unableToSubmit")}
        <br />
        {t("inquiriesContact", { phoneNo: result.enquiryPhoneNo })}
      </span>
    ),
    [RESULT.CANCELLED]: (
      <span>
        {t("cancelledAppointmentInfo")}
        <br />
        {t("inquiriesContact", { phoneNo: result.enquiryPhoneNo })}
      </span>
    ),
    [RESULT.ERROR]: result?.enquiryPhoneNo ? (
      <span>{t("inquiriesContact", { phoneNo: result.enquiryPhoneNo })}</span>
    ) : (
      ""
    ),
  };

  return (
    <div className={styles.container}>
      <div className={styles.box}>
        {Icon[result.status]}
        <div className={styles.title}>{Title[result.status]}</div>
        {Label[result.status] && (
          <div className={styles.label}>{Label[result.status]}</div>
        )}
        {result.status !== RESULT.ERROR && (
          <div
            className={styles.infoBox}
            style={{ backgroundColor: InfoBoxColor[result.status] }}
          >
            {result.status === RESULT.SUCCESS ||
            result.status === RESULT.PENDING ||
            result.status === RESULT.CANCELLED ? (
              <>
                <div>
                  <div className={styles.infoTitle}>
                    {t("applicationNumber")}
                  </div>
                  <div className={styles.infoLabel}>
                    {result.bookingNo ?? "N/A"}
                  </div>
                </div>

                <div>
                  <div className={styles.infoTitle}>
                    {t("appointmentService")}
                  </div>
                  <div className={styles.infoLabel}>
                    {result.locationName ?? "N/A"}
                  </div>
                </div>
                <div>
                  <div className={styles.infoTitle}>
                    {t("appointmentTimeSlot")}
                  </div>
                  <div className={styles.infoLabel}>
                    {result.bookingTime ?? "N/A"}
                  </div>
                </div>
                <div>
                  <div className={styles.infoTitle}>
                    {t("appointmentLocation")}
                  </div>
                  <div className={styles.infoLabel}>
                    {cmsSiteId.toUpperCase() === "AMC"
                      ? t("amcAddress")
                      : cmsSiteId.toUpperCase() === "EKMC"
                      ? t("ekmcAddress")
                      : t("bhAddress")}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={styles.failTitle}>{t("submissionReasons")}</div>
                <div className={styles.failLabels}>
                  <div className={styles.failLabelContainer}>
                    <div className={styles.dot}></div>
                    <span>{t("moreThanThreeServices")}</span>
                  </div>

                  <div className={styles.failLabelContainer}>
                    <div className={styles.dot}></div>
                    <span>{t("fullyBooked")}</span>
                  </div>

                  <div className={styles.failLabelContainer}>
                    <div className={styles.dot}></div>
                    <span>{t("unstableNetwork")}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <div
          className={`${styles.buttons} ${
            result.status === RESULT.ERROR ? styles.error : ""
          }`}
        >
          {result.status === RESULT.FAIL && (
            <Button type="secondary" onClick={onEdit}>
              {t("editTimeSlot")}
            </Button>
          )}
          {onSearchAnotherAppointment && (
            <Button type="secondary" onClick={onSearchAnotherAppointment}>
              {t("searchAnotherAppointment")}
            </Button>
          )}
          <Button type="secondary" onClick={onReStart}>
            {result.status === RESULT.FAIL || result.status === RESULT.ERROR
              ? t("restartAppointment")
              : t("applyAnotherService")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Result;
